import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import CampaignsCard from '../partials/campaigns/CampaignsCard';
import CampaignEmptyState from "./NoLiveCampaign";
import CampaignsSkeleton from "../common/CampaignsSkeleton";
import DropdownFilter from '../components/DropdownFilter';
import { useUser } from '../userContext';
import useAuth from '../dashboard/useAuth';

export default function FullWidthGrid() {
  const [campaigns, setCampaigns] = useState([]); // All campaigns loaded from the database
  const [visibleCampaigns, setVisibleCampaigns] = useState([]); // Currently visible campaigns after filtering
  const [isLoading, setIsLoading] = useState(true); // Loading state for the initial load
  const [selectedFilters, setSelectedFilters] = useState({
    draft: true,
    ready: true,
    live: true,
    complete: false, // By default, exclude completed campaigns
  });
  const { user, loading } = useAuth(); // Auth hook
  const navigate = useNavigate();
  const { businessProfile, campaigns: contextCampaigns, loading: userLoading } = useUser();

  // OPTIMIZATION: Use campaigns from UserContext instead of separate Firestore query
  useEffect(() => {
    async function fetchData() {
      try {
        if (!user || !businessProfile?.id) {
          return;
        }

        // OPTIMIZATION: Use campaigns from context if available
        if (contextCampaigns && contextCampaigns.length >= 0) {
          console.log('Using campaigns from UserContext cache');
          setCampaigns(contextCampaigns);
          applyFilters(contextCampaigns, selectedFilters);
          setIsLoading(false);
          return;
        }

        // Fallback to direct Firestore query only if context data is not available
        console.log('Fetching campaigns directly from Firestore');
        const q = query(
          collection(db, 'campaign'),
          where("companyId", "==", businessProfile.id),
          orderBy("createdAt", "desc")
        );
        const querySnapshot = await getDocs(q);
        const fetchedCampaigns = [];
        querySnapshot.forEach((doc) => {
          fetchedCampaigns.push({ id: doc.id, ...doc.data() });
        });

        // Update the campaigns and visible campaigns
        setCampaigns(fetchedCampaigns);
        applyFilters(fetchedCampaigns, selectedFilters);
      } catch (error) {
        console.error("Error fetching data: ", error);
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [user, businessProfile, contextCampaigns, navigate, selectedFilters]);

  // OPTIMIZATION: React to changes in context campaigns
  useEffect(() => {
    if (contextCampaigns && contextCampaigns.length >= 0) {
      setCampaigns(contextCampaigns);
      applyFilters(contextCampaigns, selectedFilters);
    }
  }, [contextCampaigns, selectedFilters]);

  // Handle filter changes
  const handleFilterChange = (status) => {
    const updatedFilters = {
      ...selectedFilters,
      [status]: !selectedFilters[status],
    };
    setSelectedFilters(updatedFilters);
    applyFilters(campaigns, updatedFilters); // Reapply the filters
  };

  // Apply filters to the loaded campaigns
  const applyFilters = (allCampaigns, filters) => {
    const filtered = allCampaigns.filter(campaign => filters[campaign.status]);
    setVisibleCampaigns(filtered);
  };

  // Show loading spinner or campaigns based on the state
  if (loading || userLoading || isLoading) {
    return <CampaignsSkeleton />;
  }

  return (
    <div className="max-w-7xl mx-auto" style={{fontFamily: '"Share Tech Mono", monospace'}}>
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-wider flex items-center gap-3" style={{
            color: '#F1C40F',
            textShadow: '2px 2px 0px #E67E22, 4px 4px 0px #D35400',
            WebkitTextStroke: '1px #E67E22'
          }}>
            CAMPAIGNS
          </h1>
          <p className="text-sm mt-2 font-bold hidden sm:block" style={{color: '#ECF0F1'}}>
            Manage your campaigns and track their performance!
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          {/* Filter Button */}
          <div className="w-full sm:w-auto">
            <DropdownFilter selectedFilters={selectedFilters} onFilterChange={handleFilterChange} />
          </div>

          {/* Create Campaign Button */}
          <button
            className="flex items-center justify-center gap-2 px-6 py-3 font-bold text-white rounded-md transition-all duration-100 w-full sm:w-auto"
            style={{
              background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
              border: '2px solid #F1C40F',
              textShadow: '1px 1px 0px #A93226'
            }}
            onClick={() => navigate('/studio/new')}
          >
            <span className="text-sm">🎮 NEW CAMPAIGN</span>
          </button>
        </div>
      </div>

      {/* Check if user has any campaigns */}
      {campaigns.length === 0 ? (
        <div className="text-center py-16 studio-card rounded-xl">
          <div className="text-8xl mb-6">🎮</div>
          <h3 className="text-2xl font-black mb-4 tracking-wider" style={{
            color: '#F1C40F',
            textShadow: '1px 1px 0px #E67E22'
          }}>
            NO CAMPAIGNS YET!
          </h3>
          <p className="text-lg font-bold mb-8" style={{color: '#ECF0F1'}}>
            Ready to launch your first interactive campaign and drive engagement! 🚀
          </p>
          <button
            onClick={() => navigate('/studio/new')}
            className="inline-flex items-center px-8 py-4 text-lg font-bold rounded-md text-white transition-all duration-100"
            style={{
              background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
              border: '2px solid #F1C40F',
              textShadow: '1px 1px 0px #A93226'
            }}
          >
            🎯 CREATE FIRST CAMPAIGN
          </button>
        </div>
      ) : (
        <>
          {/* Check if any campaigns match the filters */}
          {visibleCampaigns.length > 0 ? (
            <div className="grid grid-cols-12 gap-6">
              {visibleCampaigns.map((campaign) => (
                <CampaignsCard key={campaign.id} campaign={campaign} onClick={() => navigate(`/studio/campaign/${campaign.id}`, { state: { from: '/studio/campaigns' } })} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 studio-card rounded-xl">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-black mb-2" style={{color: '#F1C40F'}}>
                NO CAMPAIGNS MATCH FILTER
              </h3>
              <p className="font-bold" style={{color: '#ECF0F1'}}>
                Try adjusting your filter settings to see more campaigns.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
